import React, { useState } from 'react';
import { OfferRedemptionStats } from '../../../services/analyticsService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface TopOffersTableProps {
  data: OfferRedemptionStats[];
  title: string;
}

const TopOffersTable: React.FC<TopOffersTableProps> = ({ data, title }) => {
  const [sortBy, setSortBy] = useState<keyof OfferRedemptionStats>('totalRedemptions');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleSort = (key: keyof OfferRedemptionStats) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const getCategoryIcon = (category: string) => {
    const categoryIcons: Record<string, string> = {
      'Attractions': 'MapPin',
      'Restaurants & Cafés': 'Coffee',
      'Shopping/Retail': 'ShoppingBag',
      'Beauty & Wellness': 'Heart',
      'Activities & Experiences': 'Calendar',
      'Entertainment & Nightlife': 'Music',
      'Transport & Mobility': 'Car',
      'Local Services': 'Settings',
      'Kids & Family': 'Baby',
    };
    return categoryIcons[category] || 'Tag';
  };

  const getCategoryColor = (category: string) => {
    const categoryColors: Record<string, string> = {
      'Attractions': 'text-blue-600 bg-blue-50',
      'Restaurants & Cafés': 'text-orange-600 bg-orange-50',
      'Shopping/Retail': 'text-purple-600 bg-purple-50',
      'Beauty & Wellness': 'text-pink-600 bg-pink-50',
      'Activities & Experiences': 'text-green-600 bg-green-50',
      'Entertainment & Nightlife': 'text-red-600 bg-red-50',
      'Transport & Mobility': 'text-gray-600 bg-gray-50',
      'Local Services': 'text-indigo-600 bg-indigo-50',
      'Kids & Family': 'text-yellow-600 bg-yellow-50',
    };
    return categoryColors[category] || 'text-gray-600 bg-gray-50';
  };

  const SortIcon = ({ column }: { column: keyof OfferRedemptionStats }) => {
    if (sortBy !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return (
      <Icon 
        name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Individual offer performance metrics and revenue contribution
          </p>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <Button variant="outline" size="sm">
            <Icon name="Download" size={14} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-hidden rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Offer Details
              </th>
              <th 
                className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort('totalRedemptions')}
              >
                <div className="flex items-center space-x-1">
                  <span>Redemptions</span>
                  <SortIcon column="totalRedemptions" />
                </div>
              </th>
              <th 
                className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort('totalRevenue')}
              >
                <div className="flex items-center space-x-1">
                  <span>Revenue</span>
                  <SortIcon column="totalRevenue" />
                </div>
              </th>
              <th 
                className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort('totalSavings')}
              >
                <div className="flex items-center space-x-1">
                  <span>User Savings</span>
                  <SortIcon column="totalSavings" />
                </div>
              </th>
              <th 
                className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort('averageRedemptionValue')}
              >
                <div className="flex items-center space-x-1">
                  <span>Avg Value</span>
                  <SortIcon column="averageRedemptionValue" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((offer, index) => (
              <tr key={offer.offerId} className="border-t border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className={`p-2 rounded-lg ${getCategoryColor(offer.category)}`}>
                        <Icon name={getCategoryIcon(offer.category)} size={16} />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">
                        {offer.offerTitle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {offer.businessName}
                      </p>
                      <div className="flex items-center space-x-1 mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(offer.category)}`}>
                          {offer.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-foreground">
                    {offer.totalRedemptions.toLocaleString('de-AT')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    #{index + 1} rank
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(offer.totalRevenue)}
                  </div>
                  <div className="text-xs text-green-600">
                    +{((offer.totalRevenue / (offer.totalRevenue + offer.totalSavings)) * 100).toFixed(1)}% margin
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(offer.totalSavings)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    User benefit
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(offer.averageRedemptionValue)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Per redemption
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {sortedData.map((offer, index) => (
          <div key={offer.offerId} className="bg-muted/30 rounded-lg p-4 border border-border">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${getCategoryColor(offer.category)}`}>
                  <Icon name={getCategoryIcon(offer.category)} size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">{offer.offerTitle}</h4>
                  <p className="text-xs text-muted-foreground">{offer.businessName}</p>
                </div>
              </div>
              <span className="text-xs text-muted-foreground">#{index + 1}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Redemptions</p>
                <p className="text-sm font-medium text-foreground">{offer.totalRedemptions.toLocaleString('de-AT')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Revenue</p>
                <p className="text-sm font-medium text-foreground">{formatCurrency(offer.totalRevenue)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">User Savings</p>
                <p className="text-sm font-medium text-foreground">{formatCurrency(offer.totalSavings)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Value</p>
                <p className="text-sm font-medium text-foreground">{formatCurrency(offer.averageRedemptionValue)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {data.length === 0 && (
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No offers found</h3>
          <p className="text-muted-foreground">No redemption data available for the selected period.</p>
        </div>
      )}
    </div>
  );
};

export default TopOffersTable;